import Link from 'next/link';
import { Bell, Search } from 'lucide-react';
import { GenericEventsRouteProvider } from '@wix/data-tools-generic-events';
import type { ConversationsState } from '../../../../lib/conversations';
import type { Me } from '../../../../lib/me';
import { useBasePath } from '../../../../lib/use-base-path';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { markNavFromDrawer } from '../../../../lib/drawer-nav';
import { useLeaveChatOnNavigate } from '../../../../lib/use-leave-chat';
import { useUnreadNotifications } from '../../../../lib/use-unread-notifications';
import { useTranslation } from '../../../../lib/i18n';
import { SymphonyMark } from '../../../SymphonyMark';
import { IconButton, ScrollArea } from '../../ui';
import type { NavId } from '../hooks/use-sidebar-nav';
import { MobileFooter } from './MobileFooter';
import { MobileNav } from './MobileNav';
import { MobileRecentChats } from './MobileRecentChats';
import { MobileWorkspaces } from './MobileWorkspaces';
import styles from './MobileSidebar.module.css';

const MARK_SIZE = 20;

/**
 * The NARROW (mobile) drawer — Figma Symphony `7593:24422`.
 *
 * A dark, flat panel: brand row with search + notifications, the destination list,
 * Workspaces, Chat History, and a footer holding the credits card and the account
 * row. It shares every piece of LOGIC with the wide rail through the hooks in
 * ../hooks (destinations, workspace rows, recent chats, credits, account) and none
 * of its markup — the two brands of layout diverged too far to parameterise.
 *
 * `data-theme="dark"` is what makes the dark palette token-pure: every --ds-*
 * semantic inside resolves to its dark value (see styles/design-tokens.css and the
 * brand-scoped block in globals.css) instead of a hardcoded hex.
 */
export function MobileSidebar({
  state,
  selectedId,
  navCurrent,
  me,
  isOpen,
  onRequestClose,
  elevated = false,
  onOpenSearch,
}: {
  state: ConversationsState;
  selectedId: string | null;
  navCurrent?: NavId;
  me: Me | null;
  isOpen: boolean;
  onRequestClose?: () => void;
  /** Lifted above the full-screen chat (z50) so the drawer slides over it. */
  elevated?: boolean;
  onOpenSearch?: () => void;
}) {
  return (
    <aside
      className={`orion-sidebar ${styles.drawer}`}
      data-open={isOpen ? 'true' : 'false'}
      data-theme="dark"
      style={{
        position: 'absolute',
        insetInlineStart: 0,
        top: 0,
        bottom: 0,
        zIndex: elevated ? 60 : 6,
        transition: 'transform 420ms cubic-bezier(0.32, 0.72, 0, 1)',
      }}
    >
      <div className={styles.column}>
        <BrandRow onRequestClose={onRequestClose} onOpenSearch={onOpenSearch} />
        <ScrollArea bar="hidden" className={styles.scroll}>
          <MobileNav current={navCurrent} onClose={onRequestClose} />
          <MobileWorkspaces />
          <GenericEventsRouteProvider value="conversations">
            <MobileRecentChats state={state} selectedId={selectedId} />
          </GenericEventsRouteProvider>
          <MobileFooter me={me} />
        </ScrollArea>
      </div>
    </aside>
  );
}

// Mark + wordmark linking to Home, with search and notifications opposite —
// the design's header row, and the native mobile menu's layout.
function BrandRow({ onRequestClose, onOpenSearch }: { onRequestClose?: () => void; onOpenSearch?: () => void }) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const leaveChat = useLeaveChatOnNavigate();
  const { logPageClickBi } = useGenericBi();
  const unreadCount = useUnreadNotifications();

  return (
    <header className={styles.brandRow}>
      {/* The logo is the way Home — same handoff as the Home destination: mark the entry
          drawer-opened (so its header shows the menu), collapse the chat overlay so Home is
          visible, and close the drawer. Navigating away also closes it, but the explicit close
          covers the already-on-Home case where no route change fires. */}
      <Link
        href="/"
        as={`${basePath}/`}
        onClick={() => {
          logPageClickBi({ page_name: 'menu', button_name: 'brand_home', origin: 'menu' });
          markNavFromDrawer();
          leaveChat();
          onRequestClose?.();
        }}
        aria-label={t('nav.catchUp')}
        className={styles.brandButton}
      >
        <SymphonyMark size={MARK_SIZE} variant="light" />
        <span className={styles.wordmark}>Symphony</span>
      </Link>
      <div className={styles.brandActions}>
        <IconButton
          aria-label={t('sidebar.search.placeholderSymphony')}
          size="tiny"
          onClick={() => {
            logPageClickBi({ page_name: 'menu', button_name: 'drawer_search', origin: 'menu' });
            onOpenSearch?.();
          }}
        >
          <Search size={16} aria-hidden />
        </IconButton>
        <span className={styles.bellSlot}>
          <IconButton
            href="/notifications"
            linkAs={`${basePath}/notifications`}
            onClick={() => {
              markNavFromDrawer();
              logPageClickBi({ page_name: 'menu', button_name: 'drawer_notifications', origin: 'menu' });
              leaveChat();
            }}
            aria-label={t('nav.notifications')}
            size="tiny"
          >
            <Bell size={16} aria-hidden />
          </IconButton>
          {unreadCount > 0 && <span aria-hidden className={styles.unreadDot} />}
        </span>
      </div>
    </header>
  );
}
